import { TableCell, TableRow } from '@material-ui/core'
import { ChangeEvent, EventHandler, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AddToAutoRequestRejectionListModal } from '../addToAutoRequestRejectionListModal/AddToAutoRequestRejectionListModal'
import { RemoveFromAutoRequestRejectionListModal } from '../removeFromAutoRequestRejectionListModal/RemoveFromAutoRequestRejectionListModal'
import { ShowNotesModal } from '../../autoApprovalList/showNotesModal/ShowNotesModal'

import { AutoRequestRejectionListItem } from '@percent/admin-dashboard/api/actions/autoRequestRejectionList/autoRequestRejectionList.types'
import { Table } from '@percent/admin-dashboard/common/components'
import { Button, ButtonText, SearchInput, Spacer } from '@percent/lemonade'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { AutoRequestRejectionListTableProps } from './AutoRequestRejectionListTable.types'

export function AutoRequestRejectionListTable({
  data,
  isLoading,
  nextPage,
  previousPage,
  totalResults,
  errorMessage,
  queryParams,
  setQueryParams,
  refresh
}: Readonly<AutoRequestRejectionListTableProps>) {
  const { t } = useTranslation()
  const { userHasPermissions } = usePermissions()
  const internalFgaOrgvValidationRequestBlocklistFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_REQUEST_BLOCKLIST
  )

  const canCreate = internalFgaOrgvValidationRequestBlocklistFeatureFlag
    ? userHasPermissions('validation_request_blocklist:can_create')
    : true
  const canDelete = internalFgaOrgvValidationRequestBlocklistFeatureFlag
    ? userHasPermissions('validation_request_blocklist:can_delete')
    : true

  const [isAddToRejectionListModalOpen, setIsAddToRejectionListModalOpen] = useState(false)
  const [isShowNotesModalOpen, setIsShowNotesModalOpen] = useState(false)
  const [selectedAutoRequestRejectionEntry, setSelectedAutoRequestRejectionEntry] =
    useState<AutoRequestRejectionListItem>()
  const [selectedNotes, setSelectedNotes] = useState<string>('')
  const [searchValue, setSearchValue] = useState<string>(queryParams.query || '')

  const cells = [
    { id: 'registryId', isSortable: false },
    { id: 'registryName', isSortable: false },
    { id: 'notes', isSortable: false },
    { id: 'createdBy', isSortable: false },
    ...(canDelete ? [{ id: 'actions', isSortable: false }] : [])
  ]

  const handleKeyChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      const searchTerm = (event.target as HTMLInputElement).value

      return event.key === 'Enter'
        ? searchTerm.trim() === ''
          ? setQueryParams({ query: undefined })
          : setQueryParams({
              query: searchTerm
            })
        : null
    },
    [setQueryParams]
  )

  const handleClearValue = useCallback(() => {
    setSearchValue('')
    setQueryParams({ query: undefined })
  }, [setQueryParams])

  const openAddToAutoRequestRejectionListModal = () => setIsAddToRejectionListModalOpen(true)

  const handleShowNotesModal = (notes: string) => {
    setSelectedNotes(notes)
    setIsShowNotesModalOpen(true)
  }

  const filters = useMemo(
    () => (
      <>
        <SearchInput
          value={searchValue ?? ''}
          handleClearValue={handleClearValue}
          onKeyDown={handleKeyPress}
          onChange={handleKeyChange}
          placeholder={t('table.autoRequestRejectionListSearchPlaceholder')}
        />
        <Spacer size={4} axis="horizontal" />
        {canCreate && (
          <Button onClick={openAddToAutoRequestRejectionListModal}>
            + {t('button.addToAutoRequestRejectionList')}
          </Button>
        )}
      </>
    ),
    [searchValue, handleClearValue, handleKeyPress, t, canCreate]
  )

  return (
    <>
      <Table
        isLoading={isLoading}
        totalResults={errorMessage ? 0 : totalResults || 0}
        data={data}
        previousPage={previousPage}
        nextPage={nextPage}
        columns={cells}
        filtersContent={filters}
        emptyTableText={t('table.emptyAutoRequestRejectionList')}
      >
        {data?.map(row => (
          <TableRow key={row.id}>
            <TableCell>{row.organisationIdInRegistry}</TableCell>
            <TableCell>{row.registry.englishName}</TableCell>
            <TableCell>
              {row.notes ? (
                <ButtonText icon="compliance" onClick={() => handleShowNotesModal(row.notes!)}>
                  {t('button.seeNotes')}
                </ButtonText>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell>{row.createdBy?.email ?? '-'}</TableCell>
            {canDelete && (
              <TableCell>
                <ButtonText icon="reject" onClick={() => setSelectedAutoRequestRejectionEntry(row)}>
                  {t('button.remove')}
                </ButtonText>
              </TableCell>
            )}
          </TableRow>
        ))}
      </Table>
      <ShowNotesModal isOpen={isShowNotesModalOpen} setIsOpen={setIsShowNotesModalOpen} notes={selectedNotes} />
      <AddToAutoRequestRejectionListModal
        isOpen={isAddToRejectionListModalOpen}
        setIsOpen={setIsAddToRejectionListModalOpen}
        refresh={refresh}
      />
      <RemoveFromAutoRequestRejectionListModal
        autoRequestRejectionEntry={selectedAutoRequestRejectionEntry}
        setAutoRequestRejectionEntry={setSelectedAutoRequestRejectionEntry}
        refresh={refresh}
      />
    </>
  )
}
